import { Fragment } from "react";
import { Link, useMatches } from "react-router";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { RouteHandle } from "@/app/route-handle";

/**
 * Reads the current route's `handle.crumbs` (see route-handle.ts) via
 * React Router's `useMatches()` and renders the trail. Renders nothing for
 * a route that doesn't define one (e.g. /home, or any not-yet-built
 * placeholder that hasn't set a handle) rather than showing an empty bar.
 */
export function Breadcrumbs() {
  const matches = useMatches();

  const match = [...matches].reverse().find((m) => (m.handle as RouteHandle | undefined)?.crumbs);
  if (!match) {
    return null;
  }

  const { crumbs } = match.handle as RouteHandle;
  const trail = typeof crumbs === "function" ? crumbs(match.params) : (crumbs ?? []);
  if (trail.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {trail.map((crumb, index) => {
          const isLast = index === trail.length - 1;
          return (
            <Fragment key={`${crumb.label}-${index}`}>
              <BreadcrumbItem>
                {isLast || !crumb.href ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink render={<Link to={crumb.href}>{crumb.label}</Link>} />
                )}
              </BreadcrumbItem>
              {!isLast ? <BreadcrumbSeparator /> : null}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
